import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Loader from '../components/Loader'
import Message from '../components/Message'
import FormContainer from '../components/FormContainer'
import { confirmPasswordReset, resetPasswordConfirmReset } from '../actions/passwordResetActions'

function ResetPasswordScreen() {
    const { t } = useTranslation()
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [passwordError, setPasswordError] = useState('')

    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')

    const navigate = useNavigate()
    const dispatch = useDispatch()

    const passwordResetConfirm = useSelector(state => state.passwordResetConfirm)
    const { loading, error, success, message } = passwordResetConfirm

    useEffect(() => {
        // Check if token exists
        if (!token) {
            navigate('/forgot-password')
        }

        // Reset state when component unmounts
        return () => {
            dispatch(resetPasswordConfirmReset())
        }
    }, [token, navigate, dispatch])

    useEffect(() => {
        if (success) {
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login')
            }, 3000)
        }
    }, [success, navigate])

    const validatePassword = (pwd) => {
        if (pwd.length < 8) {
            return t('resetPassword.passwordLength')
        }
        return ''
    }

    const submitHandler = (e) => {
        e.preventDefault()
        
        // Clear previous errors
        setPasswordError('')

        // Validate password
        const pwdError = validatePassword(password)
        if (pwdError) {
            setPasswordError(pwdError)
            return
        }

        // Check if passwords match
        if (password !== confirmPassword) {
            setPasswordError(t('resetPassword.mismatch'))
            return
        }

        // Dispatch reset action
        dispatch(confirmPasswordReset(token, password))
    }

    return (
        <FormContainer>
            <h1>{t('resetPassword.title')}</h1>
            
            {success ? (
                <>
                    <Message variant='success'>
                        {message || t('resetPassword.success')}
                    </Message>
                    <p>{t('resetPassword.redirect')}</p>
                </>
            ) : (
                <>
                    <p>{t('resetPassword.description')}</p>
                    
                    {error && <Message variant='danger'>{error}</Message>}
                    {passwordError && <Message variant='danger'>{passwordError}</Message>}
                    {loading && <Loader />}

                    <Form onSubmit={submitHandler}>
                        <Form.Group controlId='password' className='my-3'>
                            <Form.Label>{t('resetPassword.newPassword')}</Form.Label>
                            <Form.Control
                                type='password'
                                placeholder={t('resetPassword.newPasswordPlaceholder')}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            ></Form.Control>
                            <Form.Text className='text-muted'>
                                {t('resetPassword.passwordLength')}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group controlId='confirmPassword' className='my-3'>
                            <Form.Label>{t('resetPassword.confirmPassword')}</Form.Label>
                            <Form.Control
                                type='password'
                                placeholder={t('resetPassword.confirmPlaceholder')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            ></Form.Control>
                        </Form.Group>

                        <Button type='submit' variant='primary' disabled={loading}>
                            {t('resetPassword.submit')}
                        </Button>
                    </Form>
                </>
            )}
        </FormContainer>
    )
}

export default ResetPasswordScreen
